// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Full-screen dialog variant for focused content viewing.
 * Use when users need to concentrate on content without distractions.
 */
import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

// modal=false so Radix doesn't focus-trap or block pointer events outside the
// dialog. The dialog is rendered to the right of the sidebar (offset by
// --app-sidebar-width), so the sidebar should stay fully interactive while
// the dialog is open. With the default modal=true, the first sidebar click
// after opening is swallowed by Radix's outside-interaction handler.
const FullscreenDialog = ({
  modal = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root modal={modal} {...props} />
)
FullscreenDialog.displayName = 'FullscreenDialog'

const FullscreenDialogTrigger = DialogPrimitive.Trigger
const FullscreenDialogClose = DialogPrimitive.Close
const FullscreenDialogPortal = DialogPrimitive.Portal

const FullscreenDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed top-10 right-0 bottom-0 z-[9999] bg-black/80 left-[var(--app-sidebar-width,0px)]',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
FullscreenDialogOverlay.displayName = 'FullscreenDialogOverlay'

const FullscreenDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <FullscreenDialogPortal>
    <FullscreenDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      // With modal=false, Radix still fires onInteractOutside for clicks
      // anywhere outside the content box — including the sidebar to the left
      // of the dialog. Suppress those so the sidebar's controls don't double
      // as dialog-close. Escape and the in-header close button still work.
      onPointerDownOutside={(e) => e.preventDefault()}
      onInteractOutside={(e) => e.preventDefault()}
      className={cn(
        'fixed top-10 right-0 bottom-0 z-[9999] flex flex-col bg-background overflow-hidden left-[var(--app-sidebar-width,0px)]',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </FullscreenDialogPortal>
))
FullscreenDialogContent.displayName = 'FullscreenDialogContent'

interface FullscreenDialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Optional content rendered on the far left of the header (e.g. sidebar toggle) */
  leading?: React.ReactNode
  /** Optional actions to render on the right side of header (before close button) */
  actions?: React.ReactNode
}

const FullscreenDialogHeader = ({
  className,
  children,
  leading,
  actions,
  ...props
}: FullscreenDialogHeaderProps) => (
  <div
    className={cn(
      'flex items-center gap-4 px-6 py-4 bg-slate-50 border-b border-slate-200 shrink-0',
      className
    )}
    {...props}
  >
    {leading && <div className="shrink-0">{leading}</div>}
    <div className="flex-1 min-w-0">{children}</div>
    {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    <DialogPrimitive.Close className="shrink-0 rounded-md p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-200 transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2">
      <X className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </DialogPrimitive.Close>
  </div>
)
FullscreenDialogHeader.displayName = 'FullscreenDialogHeader'

const FullscreenDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-slate-800 truncate', className)}
    {...props}
  />
))
FullscreenDialogTitle.displayName = 'FullscreenDialogTitle'

const FullscreenDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-slate-500 truncate', className)}
    {...props}
  />
))
FullscreenDialogDescription.displayName = 'FullscreenDialogDescription'

const FullscreenDialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex-1 flex flex-col min-h-0 overflow-auto p-6', className)}
    {...props}
  />
)
FullscreenDialogBody.displayName = 'FullscreenDialogBody'

export {
  FullscreenDialog,
  FullscreenDialogTrigger,
  FullscreenDialogClose,
  FullscreenDialogPortal,
  FullscreenDialogOverlay,
  FullscreenDialogContent,
  FullscreenDialogHeader,
  FullscreenDialogTitle,
  FullscreenDialogDescription,
  FullscreenDialogBody,
}
